import {
  type ApiTokenCreated,
  type ApiTokenRow,
} from "@nexys/user-management-client";
import { AlertCircle, Check, Copy, KeySquare, Plus, ShieldOff } from "lucide-react";
import { useState, type FormEvent } from "react";

import { useUmClient } from "../../context/auth-context.js";
import { useCall } from "../../hooks/use-call.js";
import { errorMessage } from "../../lib/error-message.js";
import { formatRelative } from "../../lib/utils.js";
import { EmptyState } from "../primitives/empty-state.js";
import { Alert, AlertDescription } from "../ui/alert.js";
import { Badge } from "../ui/badge.js";
import { Button } from "../ui/button.js";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card.js";
import { Input } from "../ui/input.js";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table.js";

const StatusBadge = ({ status }: { status: ApiTokenRow["status"] }) => (
  <Badge variant={status === "active" ? "secondary" : "outline"}>
    {status}
  </Badge>
);

/**
 * Personal API tokens — long-lived `um_…` credentials for CLIs and
 * scripts. The plaintext is shown exactly once after creation.
 */
export const ApiTokensCard = () => {
  const client = useUmClient();
  const { data, loading, error, refresh } = useCall("listApiTokens");
  const [name, setName] = useState("");
  const [expiresInDays, setExpiresInDays] = useState("");
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<ApiTokenCreated | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const tokens = data?.tokens ?? [];

  const create = async (e: FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setActionError(null);
    const days = expiresInDays.trim() ? Number(expiresInDays.trim()) : undefined;
    const res = await client.createApiToken({
      name: name.trim(),
      expiresInDays: days,
    });
    setCreating(false);
    if (res.ok) {
      setCreated(res.data);
      setName("");
      setExpiresInDays("");
      await refresh();
    } else {
      setActionError(errorMessage(res.error, "Couldn't create the token."));
    }
  };

  const revoke = async (t: ApiTokenRow) => {
    setBusy(t.id);
    setActionError(null);
    const res = await client.revokeApiToken({ id: t.id });
    setBusy(null);
    if (res.ok) {
      await refresh();
    } else {
      setActionError(errorMessage(res.error, "Couldn't revoke the token."));
    }
  };

  const copyToken = async () => {
    if (!created) return;
    await navigator.clipboard.writeText(created.token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <KeySquare className="size-4 text-muted-foreground" />
          API tokens
        </CardTitle>
        <CardDescription>
          Long-lived credentials for CLIs and scripts. Use them as{" "}
          <code className="font-mono text-xs">Authorization: Bearer um_…</code>.
          Revoked tokens stay listed until the retention policy removes them.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        {error ? (
          <Alert variant="destructive">
            <AlertCircle />
            <AlertDescription>{errorMessage(error)}</AlertDescription>
          </Alert>
        ) : null}
        {actionError ? (
          <Alert variant="destructive">
            <AlertCircle />
            <AlertDescription>{actionError}</AlertDescription>
          </Alert>
        ) : null}

        {created ? (
          <div className="grid gap-2 rounded-lg border border-dashed bg-muted/30 p-4">
            <p className="text-sm font-medium">
              Copy your new token now — it won't be shown again.
            </p>
            <div className="flex items-center gap-2">
              <code
                data-testid="api-token-plaintext"
                className="flex-1 break-all rounded bg-muted px-2 py-1 font-mono text-xs"
              >
                {created.token}
              </code>
              <Button variant="outline" size="sm" onClick={copyToken}>
                {copied ? <Check className="text-green-600" /> : <Copy />}
                {copied ? "Copied" : "Copy"}
              </Button>
              <Button size="sm" onClick={() => setCreated(null)}>
                Done
              </Button>
            </div>
          </div>
        ) : (
          <form
            onSubmit={create}
            className="grid items-end gap-3 sm:grid-cols-[1fr_auto_auto]"
          >
            <div className="grid gap-1.5">
              <label htmlFor="token-name" className="text-sm font-medium leading-none">
                Token name
              </label>
              <Input
                id="token-name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. CLI on work laptop"
              />
            </div>
            <div className="grid gap-1.5">
              <label htmlFor="token-expiry" className="text-sm font-medium leading-none">
                Expires (days, optional)
              </label>
              <Input
                id="token-expiry"
                type="number"
                min={1}
                max={3650}
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(e.target.value)}
                placeholder="90"
                className="w-36"
              />
            </div>
            <Button type="submit" loading={creating} disabled={!name.trim()}>
              <Plus />
              Create token
            </Button>
          </form>
        )}

        {loading ? null : tokens.length === 0 ? (
          <EmptyState
            icon={KeySquare}
            title="No API tokens"
            description="Create one to authenticate scripts and CLI tools."
          />
        ) : (
          <div className="overflow-hidden rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Token</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last used</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tokens.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="font-medium">{t.name ?? "—"}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {t.start ? `${t.start}…` : "—"}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={t.status} />
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {t.lastUsedAt ? formatRelative(t.lastUsedAt) : "Never"}
                    </TableCell>
                    <TableCell className="text-right">
                      {t.status === "active" ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => revoke(t)}
                          loading={busy === t.id}
                          aria-label={`Revoke token ${t.name ?? t.id}`}
                        >
                          <ShieldOff className="text-destructive" />
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
